import { useContext } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  activateMembers,
  fetchActivePlansInfo,
  fetchClientInfo,
  fetchFlatFileAccessToken,
  fetchMembersData,
  fetchPlansDiscounts,
  postCreateMultipleBillingRecords,
  postAssignPlanToMember,
  postMembers,
  postRejectPaymentMethod,
  postSendEmailToMembers,
  startPaymentMatching,
  updateStripeId,
  updateUploadedUser,
  postCreateSingleBillingRecord,
} from "./migrationAssistantApi";
import type {
  FetchFlatFileAccessTokenDataType,
  MemberDataType,
  PostCreateMultipleBillingRecordsType,
  PostActivateMembersType,
  PostAssignPlanType,
  PostMigratingMembersPayloadType,
  UploadedUserUpdatePayload,
  PostCreateSingleBillingRecordType,
} from "../types";
import { AppContext } from "../../../context/app";
import {
  getStripeBillingData,
  postStripeCustomerData,
} from "../../../apis/billingApi";

export function useGetFlatFileAccessToken() {
  return useQuery<FetchFlatFileAccessTokenDataType>({
    queryKey: ["fetchFlatFileAccessToken"],
    queryFn: () => fetchFlatFileAccessToken(),
  });
}

export function useGetMembersData() {
  return useQuery<MemberDataType[]>({
    queryKey: ["fetchMigratingMembers"],
    queryFn: () => fetchMembersData(),
  });
}

export function useStartPaymentMatching() {
  return useMutation({
    mutationFn: () => startPaymentMatching(),
  });
}

export function useMutateMembers() {
  return useMutation({
    mutationFn: (payload: PostMigratingMembersPayloadType) =>
      postMembers(payload),
  });
}

export function useActivePlansQuery() {
  const app = useContext(AppContext);
  return useQuery({
    queryKey: ["fetchActivePlans"],
    queryFn: () => fetchActivePlansInfo(app.client.uuid),
  });
}

export function usePlansDiscountsQuery() {
  const app = useContext(AppContext);
  return useQuery({
    queryKey: ["fetchPlansDiscounts"],
    queryFn: () => fetchPlansDiscounts(app.client.uuid),
  });
}

export function useAssignPlan() {
  return useMutation({
    mutationFn: (payload: PostAssignPlanType) =>
      postAssignPlanToMember(payload),
  });
}

export function useActivateMembers(enableAsyncActivation: boolean) {
  // Remove non-async after async is fully implemented and working
  return useMutation({
    mutationFn: (payload: PostActivateMembersType) =>
      activateMembers(payload, enableAsyncActivation),
  });
}

export function useClientInfoQuery() {
  const app = useContext(AppContext);
  return useQuery({
    queryKey: ["fetchClientSettings"],
    queryFn: () => fetchClientInfo(app.client.uuid),
  });
}

export function usePostStripeCustomerData() {
  return useMutation({
    mutationFn: (payload: { customerIds: string[] }) =>
      postStripeCustomerData(payload),
  });
}

export function useGetStripeBillingData() {
  return useQuery({
    queryKey: ["fetchStripeBillingData"],
    queryFn: () => getStripeBillingData(),
  });
}

export function usePostCreateMultipleBillingRecords() {
  return useMutation({
    mutationFn: (payload: PostCreateMultipleBillingRecordsType) =>
      postCreateMultipleBillingRecords(payload),
  });
}

export function usePostCreateSingleBillingRecord() {
  return useMutation({
    mutationFn: (payload: PostCreateSingleBillingRecordType) =>
      postCreateSingleBillingRecord(payload),
  });
}

export function usePostRejectPaymentMethod() {
  return useMutation({
    mutationFn: (userId: number) => postRejectPaymentMethod(userId),
  });
}

export function useSendEmailToMembers() {
  return useMutation({
    mutationFn: (payload: number[]) => postSendEmailToMembers(payload),
  });
}

export function useUpdateUploadedUser() {
  return useMutation({
    mutationFn: ({
      userId,
      payload,
    }: { userId: number; payload: UploadedUserUpdatePayload }) =>
      updateUploadedUser(userId, payload),
  });
}

export function useUpdateStripeId() {
  return useMutation({
    mutationFn: ({ userId, stripeId }: { userId: number; stripeId: string }) =>
      updateStripeId(userId, stripeId),
  });
}
